/**
 * Où en est l'élève connecté sur chaque exercice d'une SKILLCARD : combien de
 * fois il l'a terminé, son meilleur score, et s'il en a un en cours (à
 * reprendre). Lu sous la RLS `attempts` (l'élève ne lit que ses essais).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface ExerciseAttemptRow {
  exercise_id: string | null;
  status: string;
  score: number | null;
  started_at: string;
}

export interface ExerciseAttemptSummary {
  /** Essais terminés. */
  completed: number;
  /** Meilleur score (%) des essais terminés, null si aucun. */
  bestScore: number | null;
  /** Le dernier essai commencé n'est pas fini : à reprendre. */
  inProgress: boolean;
  /**
   * Terminé EN ENTIER aujourd'hui (règle serveur `exercise_done_today`, 0660) :
   * l'exercice ne se refait que demain.
   */
  doneToday?: boolean;
}

/** Regroupe les essais par exercice. Les essais abandonnés ne comptent pas. PUR. */
export function summarizeExerciseAttempts(
  rows: readonly ExerciseAttemptRow[],
): Record<string, ExerciseAttemptSummary> {
  const out: Record<string, ExerciseAttemptSummary & { lastStart: string }> = {};
  for (const r of rows) {
    if (!r.exercise_id || r.status === 'abandoned') continue;
    const s = (out[r.exercise_id] ??= {
      completed: 0,
      bestScore: null,
      inProgress: false,
      lastStart: '',
    });
    if (r.status === 'completed') {
      s.completed += 1;
      if (r.score != null) s.bestScore = Math.max(s.bestScore ?? 0, Math.round(r.score));
    }
    // Seul le PLUS RÉCENT essai décide : un vieil essai resté ouvert sous un
    // essai terminé depuis n'est pas « à continuer ».
    if (r.started_at >= s.lastStart) {
      s.lastStart = r.started_at;
      s.inProgress = r.status === 'in_progress';
    }
  }
  return Object.fromEntries(Object.entries(out).map(([id, { lastStart: _drop, ...s }]) => [id, s]));
}

export type ExerciseAttemptBadge =
  | { kind: 'continue'; text: string }
  | { kind: 'done'; text: string };

/** Pastille de la liste : « À continuer », « Fait 2× · 90 % », ou rien. PUR. */
export function exerciseAttemptBadge(
  s: ExerciseAttemptSummary | undefined,
): ExerciseAttemptBadge | null {
  if (!s) return null;
  const done = s.completed > 0 ? `fait ${s.completed}×` : null;
  if (s.inProgress)
    return { kind: 'continue', text: done ? `À continuer · ${done}` : 'À continuer' };
  if (s.completed === 0) return null;
  const score = s.bestScore != null ? ` · ${s.bestScore} %` : '';
  return { kind: 'done', text: `Fait ${s.completed}×${score}` };
}

/** Marque les exercices faits aujourd'hui (liste renvoyée par le serveur). PUR. */
export function withDoneToday(
  summary: Record<string, ExerciseAttemptSummary>,
  doneTodayIds: readonly string[],
): Record<string, ExerciseAttemptSummary> {
  const out = { ...summary };
  for (const id of doneTodayIds) {
    out[id] = {
      ...(out[id] ?? { completed: 1, bestScore: null, inProgress: false }),
      doneToday: true,
    };
  }
  return out;
}

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;

export function useMyExerciseAttempts(userId: string | null | undefined, exerciseIds: string[]) {
  const ids = [...exerciseIds].sort();
  return useQuery({
    queryKey: ['my-exercise-attempts', userId, ids],
    enabled: !!userId && ids.length > 0,
    queryFn: async () => {
      const [attempts, today] = await Promise.all([
        supabase
          .from('attempts')
          .select('exercise_id, status, score, started_at')
          .eq('student_id', userId!)
          .eq('session_type', 'exercise')
          .in('exercise_id', ids),
        // RPC postérieure aux types générés (0660).
        (supabase.rpc as unknown as UntypedRpc)('my_exercises_done_today', {
          p_exercise_ids: ids,
        }),
      ]);
      if (attempts.error) throw new Error(attempts.error.message);
      const summary = summarizeExerciseAttempts((attempts.data ?? []) as ExerciseAttemptRow[]);
      // Liste « faits aujourd'hui » indisponible : on n'empêche rien côté écran.
      const done = today.error ? [] : ((today.data as string[] | null) ?? []);
      return withDoneToday(summary, done);
    },
  });
}
